import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Pencil, X, Search, Merge, Hash } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import Paginacion from '../components/Paginacion';
import Modal from '../components/Modal';
import { clasesParaTipo, etiquetaClase } from '../utils/clasesResultado';
import BotonAyuda from '../components/BotonAyuda';
import { etiquetaCategoria } from '../utils/categorias';
import { useSearchParams } from 'react-router-dom';
import ReglasCategorizacion from '../components/categorias/ReglasCategorizacion';

const FORM_VACIO = { codigo: '', nombre: '', tipo: 'egreso', padre_id: '', requiere_pago_factura: false, no_es_gasto: false };
const FORM_MOV_VACIO = { cuenta_id: '', categoria_id: '', tipo: 'egreso', monto: '', fecha: '', descripcion: '', referencia: '' };

function CodigoCategoria({ categoria }) {
  if (!categoria.codigo) return null;
  return <span className="mr-2 font-mono text-xs font-normal text-slate-400 tabular-nums">{categoria.codigo}</span>;
}

export default function Categorias() {
  const { empresaActiva, empresas, rolActual } = useAuth();
  // Proponer códigos y fusionar los exige el servidor a contador o más.
  const puedeOrdenar = ['contador', 'gerente', 'admin'].includes(rolActual);
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  // Pestaña "Reglas" (#80): ?reglas=nueva&texto=…&categoria_id=… la abre
  // con el formulario lleno (desde un movimiento o desde el cierre).
  const [params, setParams] = useSearchParams();
  const [pestana, setPestana] = useState(params.get('reglas') ? 'reglas' : 'cuentas');
  const prefillRegla = params.get('reglas') === 'nueva'
    ? { texto: params.get('texto') || '', categoria_id: params.get('categoria_id') || '' }
    : null;

  const [categorias, setCategorias] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('');
  const [soloSinClasificar, setSoloSinClasificar] = useState(false);
  // Fusión de una categoría repetida en otra (#74).
  const [fusionando, setFusionando] = useState(null);
  const [destinoFusion, setDestinoFusion] = useState('');
  const [errorFusion, setErrorFusion] = useState('');
  const [guardandoFusion, setGuardandoFusion] = useState(false);
  const [proponiendo, setProponiendo] = useState(false);

  // Panel "movimientos de esta categoría": ver/editar/eliminar sin tener
  // que ir a buscarlos a Movimientos.
  const [categoriaVista, setCategoriaVista] = useState(null);
  const [movimientosCat, setMovimientosCat] = useState([]);
  const [cargandoCat, setCargandoCat] = useState(false);
  const [paginaCat, setPaginaCat] = useState(1);
  const [totalCat, setTotalCat] = useState(0);
  const [totalPaginasCat, setTotalPaginasCat] = useState(1);
  const [editandoMovId, setEditandoMovId] = useState(null);
  const [formMov, setFormMov] = useState(FORM_MOV_VACIO);
  // Cuenta original del movimiento en edición: puede quedarse en ella aunque
  // esté inactiva, pero no pasarse a otra inactiva (#147).
  const [cuentaPreviaMov, setCuentaPreviaMov] = useState(null);
  const [errorMov, setErrorMov] = useState('');
  const scrollPosRef = useRef(0);

  async function cargar() {
    const { data } = await api.get('/categorias');
    setCategorias(data);
  }

  useEffect(() => {
    if (empresaActiva) {
      cargar();
      api.get('/cuentas').then(({ data }) => setCuentas(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  useEffect(() => {
    if (!categoriaVista) return;
    setCargandoCat(true);
    api
      .get(`/categorias/${categoriaVista.id}/movimientos`, { params: { pagina: paginaCat } })
      .then(({ data }) => {
        setMovimientosCat(data.datos);
        setTotalCat(data.total);
        setTotalPaginasCat(data.totalPaginas);
      })
      .finally(() => setCargandoCat(false));
  }, [categoriaVista, paginaCat]);

  function verMovimientos(categoria) {
    const contenedor = document.querySelector('main');
    scrollPosRef.current = contenedor ? contenedor.scrollTop : window.scrollY;
    setCategoriaVista(categoria);
    setPaginaCat(1);
    setEditandoMovId(null);
    setErrorMov('');
  }

  function cerrarVistaMovimientos() {
    setCategoriaVista(null);
    setMovimientosCat([]);
    setEditandoMovId(null);
    // El modal fixed no debería afectar el scroll de la página de atrás,
    // pero algunos navegadores lo resetean igual al desmontarlo — se
    // restaura a mano el scroll que tenía antes de abrirlo.
    requestAnimationFrame(() => {
      const contenedor = document.querySelector('main');
      if (contenedor) contenedor.scrollTop = scrollPosRef.current;
      else window.scrollTo(0, scrollPosRef.current);
    });
  }

  function empezarEdicionMov(m) {
    setEditandoMovId(m.id);
    setCuentaPreviaMov(m.cuenta_id);
    setFormMov({
      cuenta_id: m.cuenta_id,
      categoria_id: m.categoria_id || '',
      tipo: m.tipo,
      monto: m.monto,
      fecha: m.fecha,
      descripcion: m.descripcion || '',
      referencia: m.referencia || '',
      cliente_id: m.cliente_id || '',
      tasa_override: m.tasa_override || ''
    });
    setErrorMov('');
  }

  async function guardarMov(e) {
    e.preventDefault();
    setErrorMov('');
    try {
      await api.put(`/movimientos/${editandoMovId}`, formMov);
      setEditandoMovId(null);
      const { data } = await api.get(`/categorias/${categoriaVista.id}/movimientos`, { params: { pagina: paginaCat } });
      setMovimientosCat(data.datos);
      setTotalCat(data.total);
      setTotalPaginasCat(data.totalPaginas);
      cargar();
    } catch (err) {
      setErrorMov(err.response?.data?.error || 'No se pudo guardar el movimiento');
    }
  }

  async function eliminarMov(id) {
    if (!window.confirm('¿Eliminar este movimiento? Esta acción quedará registrada en la bitácora.')) return;
    try {
      await api.delete(`/movimientos/${id}`);
      const { data } = await api.get(`/categorias/${categoriaVista.id}/movimientos`, { params: { pagina: paginaCat } });
      setMovimientosCat(data.datos);
      setTotalCat(data.total);
      setTotalPaginasCat(data.totalPaginas);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el movimiento');
    }
  }

  function empezarEdicion(categoria) {
    setEditandoId(categoria.id);
    setForm({
      codigo: categoria.codigo || '',
      nombre: categoria.nombre,
      tipo: categoria.tipo,
      padre_id: categoria.padre_id || '',
      requiere_pago_factura: !!categoria.requiere_pago_factura,
      no_es_gasto: !!categoria.no_es_gasto
    });
    setError('');
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setEditandoId(null);
    setForm(FORM_VACIO);
    setError('');
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    if (!form.nombre.trim()) return;
    try {
      const datos = {
        codigo: form.codigo.trim(),
        nombre: form.nombre.trim(),
        tipo: form.tipo,
        padre_id: form.padre_id || null,
        requiere_pago_factura: form.requiere_pago_factura,
        no_es_gasto: form.tipo === 'egreso' && form.no_es_gasto
      };
      if (editandoId) {
        await api.put(`/categorias/${editandoId}`, datos);
      } else {
        await api.post('/categorias', datos);
      }
      cancelar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la categoría');
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar esta categoría? Los movimientos y sub-categorías que la usan quedarán sin categoría.')) return;
    try {
      await api.delete(`/categorias/${id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar la categoría');
    }
  }

  // ---------- Código contable y limpieza (#74) ----------

  const totalSinCodigo = categorias.filter((c) => !c.codigo).length;

  async function proponerCodigos() {
    if (!window.confirm(`Se asigna un código a ${totalSinCodigo === 1 ? 'la categoría que no tiene' : `las ${totalSinCodigo} categorías que no tienen`}: 4.NN los ingresos, 5.NN los egresos y, a cada sub-categoría, el de su principal más .NN. Los códigos que ya hay no cambian y después puedes corregir cualquiera. ¿Seguir?`)) return;
    setProponiendo(true);
    try {
      await api.post('/categorias/proponer-codigos');
      await cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudieron proponer los códigos');
    } finally {
      setProponiendo(false);
    }
  }

  function abrirFusion(categoria) {
    setFusionando(categoria);
    setDestinoFusion('');
    setErrorFusion('');
  }

  async function confirmarFusion(e) {
    e.preventDefault();
    const destino = categorias.find((c) => String(c.id) === String(destinoFusion));
    if (!destino) return;
    const hijas = categorias.filter((c) => c.padre_id === fusionando.id).length;
    const texto = `¿Fusionar "${etiquetaCategoria(fusionando)}" en "${etiquetaCategoria(destino)}"?\n\n`
      + `${fusionando.total_movimientos} movimientos, sus líneas de facturas y sus productos pasan a "${destino.nombre}"`
      + `${hijas ? ` y sus ${hijas} sub-categorías pasan a colgar de ella` : ''}. "${fusionando.nombre}" se elimina.\n\nQueda registrado en Auditoría.`;
    if (!window.confirm(texto)) return;
    setGuardandoFusion(true);
    setErrorFusion('');
    try {
      await api.post(`/categorias/${fusionando.id}/fusionar`, { destino_id: destino.id });
      setFusionando(null);
      await cargar();
    } catch (err) {
      setErrorFusion(err.response?.data?.error || 'No se pudo fusionar');
    } finally {
      setGuardandoFusion(false);
    }
  }

  // Destinos posibles: mismo tipo, ni ella misma ni sus sub-categorías; si
  // tiene sub-categorías, solo principales (máximo 2 niveles).
  const destinosFusion = fusionando
    ? categorias.filter((c) => c.tipo === fusionando.tipo && c.id !== fusionando.id && c.padre_id !== fusionando.id
        && (!categorias.some((h) => h.padre_id === fusionando.id) || !c.padre_id))
    : [];

  // ---------- Clasificación para los estados financieros (#110) ----------

  // Se guarda al cambiar el select, sin abrir el formulario; la lista se
  // actualiza en memoria para no perder el scroll de 500+ categorías.
  async function clasificar(categoria, clase) {
    try {
      const { data } = await api.patch(`/categorias/${categoria.id}/clase`, { clase_resultado: clase || null });
      setCategorias((prev) => prev.map((c) => (c.id === data.id ? { ...c, clase_resultado: data.clase_resultado } : c)));
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar la clasificación');
    }
  }

  const categoriaPorId = (id) => categorias.find((c) => String(c.id) === String(id));
  // Una hija sin clase hereda la del padre.
  const claseEfectiva = (c) => c.clase_resultado || (c.padre_id ? categoriaPorId(c.padre_id)?.clase_resultado : null) || null;
  const totalSinClasificar = categorias.filter((c) => !claseEfectiva(c)).length;

  function SelectorClase({ categoria }) {
    const padre = categoria.padre_id ? categoriaPorId(categoria.padre_id) : null;
    const heredada = padre?.clase_resultado ? etiquetaClase(padre.clase_resultado, categoria.tipo) : null;
    const sinClase = !claseEfectiva(categoria);
    return (
      <select
        value={categoria.clase_resultado || ''}
        onChange={(e) => clasificar(categoria, e.target.value)}
        title="Renglón en el Estado de resultados / Balance general (#110)"
        className={`border rounded-md px-1.5 py-0.5 text-xs max-w-[190px] ${sinClase ? 'border-amber-300 bg-amber-50 text-amber-700' : 'text-slate-500'}`}
      >
        <option value="">{heredada ? `Hereda: ${heredada}` : 'Sin clasificar'}</option>
        {['Estado de resultados', 'Balance', 'No afecta'].map((grupo) => {
          const opciones = clasesParaTipo(categoria.tipo).filter((c) => c.grupo === grupo);
          if (opciones.length === 0) return null;
          return (
            <optgroup key={grupo} label={grupo}>
              {opciones.map((c) => (
                <option key={c.value} value={c.value}>{c.label[categoria.tipo]}</option>
              ))}
            </optgroup>
          );
        })}
      </select>
    );
  }

  // Solo las categorías de primer nivel pueden ser "padre" (máximo 2 niveles).
  const categoriasPrincipalesDe = (tipo) => categorias.filter((c) => !c.padre_id && c.tipo === tipo);
  const subCategoriasDe = (padreId) => categorias.filter((c) => String(c.padre_id) === String(padreId));

  const filtroNormalizado = filtro.trim().toLowerCase();

  function Seccion({ tipo, titulo }) {
    let principales = categoriasPrincipalesDe(tipo);

    if (filtroNormalizado) {
      principales = principales
        .map((padre) => {
          const padreCoincide = etiquetaCategoria(padre).toLowerCase().includes(filtroNormalizado);
          const hijosQueCoinciden = subCategoriasDe(padre.id).filter((hijo) =>
            etiquetaCategoria(hijo).toLowerCase().includes(filtroNormalizado)
          );
          if (!padreCoincide && hijosQueCoinciden.length === 0) return null;
          return { ...padre, hijosFiltrados: padreCoincide ? subCategoriasDe(padre.id) : hijosQueCoinciden };
        })
        .filter(Boolean);
    } else {
      principales = principales.map((padre) => ({ ...padre, hijosFiltrados: subCategoriasDe(padre.id) }));
    }

    if (soloSinClasificar) {
      principales = principales
        .map((padre) => ({ ...padre, hijosFiltrados: padre.hijosFiltrados.filter((h) => !claseEfectiva(h)) }))
        .filter((padre) => !claseEfectiva(padre) || padre.hijosFiltrados.length > 0);
    }

    return (
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h2 className="font-semibold text-slate-700 mb-3">{titulo}</h2>
        {principales.length === 0 && (
          <p className="text-sm text-slate-400">
            {filtroNormalizado || soloSinClasificar ? 'Ninguna categoría coincide con el filtro.' : 'No hay categorías creadas todavía.'}
          </p>
        )}
        <div className="space-y-4">
          {principales.map((padre) => (
            <div key={padre.id}>
              <div className="flex items-center justify-between rounded-md px-2 -mx-2 py-0.5 hover:bg-slate-50 transition-colors">
                <span className="font-medium text-slate-700">
                  <CodigoCategoria categoria={padre} />
                  {padre.nombre}
                  {!!padre.no_es_gasto && <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-normal align-middle">No es gasto</span>}
                </span>
                <div className="flex items-center gap-3">
                  <SelectorClase categoria={padre} />
                  <button
                    onClick={() => verMovimientos(padre)}
                    title="Ver movimientos de esta categoría"
                    className="text-xs text-slate-400 hover:text-primary-600 tabular-nums underline decoration-dotted"
                  >
                    {padre.total_movimientos} mov.
                  </button>
                  <button onClick={() => empezarEdicion(padre)} title="Editar" className="text-slate-400 hover:text-primary-600">
                    <Pencil size={16} />
                  </button>
                  {puedeOrdenar && (
                    <button onClick={() => abrirFusion(padre)} title="Fusionar con otra categoría" className="text-slate-400 hover:text-primary-600">
                      <Merge size={16} />
                    </button>
                  )}
                  <button onClick={() => eliminar(padre.id)} title="Eliminar" className="text-slate-400 hover:text-red-600">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="ml-4 mt-1 space-y-1 border-l border-slate-200 pl-3">
                {padre.hijosFiltrados.map((hijo) => (
                  <div key={hijo.id} className="flex items-center justify-between text-sm text-slate-600 rounded-md px-2 -mx-2 py-0.5 hover:bg-slate-50 transition-colors">
                    <span>
                      <CodigoCategoria categoria={hijo} />
                      {hijo.nombre}
                      {!!hijo.no_es_gasto && <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-normal align-middle">No es gasto</span>}
                    </span>
                    <div className="flex items-center gap-3">
                      <SelectorClase categoria={hijo} />
                      <button
                        onClick={() => verMovimientos(hijo)}
                        title="Ver movimientos de esta categoría"
                        className="text-xs text-slate-400 hover:text-primary-600 tabular-nums underline decoration-dotted"
                      >
                        {hijo.total_movimientos} mov.
                      </button>
                      <button onClick={() => empezarEdicion(hijo)} title="Editar" className="text-slate-400 hover:text-primary-600">
                        <Pencil size={14} />
                      </button>
                      {puedeOrdenar && (
                        <button onClick={() => abrirFusion(hijo)} title="Fusionar con otra categoría" className="text-slate-400 hover:text-primary-600">
                          <Merge size={14} />
                        </button>
                      )}
                      <button onClick={() => eliminar(hijo.id)} title="Eliminar" className="text-slate-400 hover:text-red-600">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                {padre.hijosFiltrados.length === 0 && (
                  <p className="text-xs text-slate-400">
                    {filtroNormalizado ? 'Ninguna sub-categoría coincide con el filtro.' : 'Sin sub-categorías.'}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const padresDelTipoSeleccionado = categoriasPrincipalesDe(form.tipo).filter((c) => c.id !== editandoId);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Cuentas Contables
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        {pestana === 'cuentas' && (
          <button
            onClick={() => setMostrarForm(true)}
            title="Nueva categoría"
            className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md self-start sm:self-auto"
          >
            <Plus size={18} />
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[['cuentas', 'Cuentas'], ['reglas', 'Reglas automáticas']].map(([k, t]) => (
          <button key={k} type="button" onClick={() => setPestana(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${pestana === k ? 'border-primary-600 text-primary-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t}
          </button>
        ))}
      </div>

      {pestana === 'reglas' ? (
        <ReglasCategorizacion
          categorias={categorias}
          cuentas={cuentas}
          puede={puedeOrdenar}
          prefill={prefillRegla && categorias.length ? prefillRegla : null}
          onPrefillUsado={() => setParams({}, { replace: true })}
        />
      ) : (<>
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative w-full max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar categoría o sub-categoría..."
            className="border rounded-md pl-8 pr-2.5 py-1.5 w-full text-sm"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={soloSinClasificar}
            onChange={(e) => setSoloSinClasificar(e.target.checked)}
            className="rounded border-slate-300"
          />
          Solo sin clasificar
          <span className={`text-xs px-2 py-0.5 rounded-full ${totalSinClasificar ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
            {totalSinClasificar}
          </span>
        </label>
        {puedeOrdenar && totalSinCodigo > 0 && (
          <button
            onClick={proponerCodigos}
            disabled={proponiendo}
            className="flex items-center gap-1.5 text-sm border border-slate-300 rounded-md px-3 py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            title="Asigna un código contable a las categorías que no tienen"
          >
            <Hash size={14} />
            {proponiendo ? 'Asignando…' : `Proponer códigos (${totalSinCodigo} sin código)`}
          </button>
        )}
      </div>

      {mostrarForm && (
        <Modal title={editandoId ? 'Editando categoría' : 'Nueva categoría'} onClose={cancelar}>
        <form onSubmit={guardar} className="flex flex-wrap items-end gap-2">
          <div className="w-32">
            <label className="block text-xs text-slate-500 mb-1">Código (opcional)</label>
            <input
              placeholder="Ej. 5.01.03"
              inputMode="decimal"
              value={form.codigo}
              onChange={(e) => setForm({ ...form, codigo: e.target.value.replace(/[^0-9.]/g, '') })}
              className="border rounded-md px-3 py-2 w-full font-mono tabular-nums"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-slate-500 mb-1">Nombre</label>
            <input
              required
              placeholder="Ej. Gastos Administrativos, Honorarios Profesionales"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="border rounded-md px-3 py-2 w-full"
            />
          </div>
          <div className="min-w-[140px]">
            <label className="block text-xs text-slate-500 mb-1">Tipo</label>
            <select
              value={form.tipo}
              onChange={(e) => setForm({ ...form, tipo: e.target.value, padre_id: '' })}
              className="border rounded-md px-3 py-2 w-full"
            >
              <option value="egreso">Egreso</option>
              <option value="ingreso">Ingreso</option>
            </select>
          </div>
          <div className="min-w-[200px]">
            <label className="block text-xs text-slate-500 mb-1">Sub-categoría de (opcional)</label>
            <select
              value={form.padre_id}
              onChange={(e) => setForm({ ...form, padre_id: e.target.value })}
              className="border rounded-md px-3 py-2 w-full"
            >
              <option value="">Categoría de primer nivel</option>
              {padresDelTipoSeleccionado.map((c) => (
                <option key={c.id} value={c.id}>{etiquetaCategoria(c)}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 min-w-[220px]">
            <input
              type="checkbox"
              checked={form.requiere_pago_factura}
              onChange={(e) => setForm({ ...form, requiere_pago_factura: e.target.checked })}
              className="rounded border-slate-300"
            />
            Requiere pago de factura (conciliación #79)
          </label>
          {form.tipo === 'egreso' && (
            <label className="flex items-center gap-2 text-sm text-slate-600 min-w-[220px]" title="Sale del 100% en Egresos por categoría (ej. Retiro socios). Marcarla en el grupo excluye también sus sub-categorías.">
              <input
                type="checkbox"
                checked={form.no_es_gasto}
                onChange={(e) => setForm({ ...form, no_es_gasto: e.target.checked })}
                className="rounded border-slate-300"
              />
              No es gasto (fuera del 100% en Egresos por categoría)
            </label>
          )}
          <button type="submit" className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700">
            {editandoId ? 'Guardar cambios' : 'Agregar'}
          </button>
          {error && <p className="text-red-600 text-sm w-full">{error}</p>}
        </form>
        </Modal>
      )}

      {fusionando && (
        <Modal title={`Fusionar "${etiquetaCategoria(fusionando)}"`} onClose={() => setFusionando(null)}>
          <form onSubmit={confirmarFusion} className="space-y-3">
            <p className="text-sm text-slate-600">
              Para limpiar categorías repetidas. Todo lo que usa "{fusionando.nombre}" ({fusionando.total_movimientos} movimientos,
              líneas de facturas y productos) pasa a la categoría que elijas; sus sub-categorías pasan a colgar de ella
              y "{fusionando.nombre}" se elimina. Queda registrado en Auditoría.
            </p>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Pasar todo a</label>
              <select
                required
                value={destinoFusion}
                onChange={(e) => setDestinoFusion(e.target.value)}
                className="border rounded-md px-3 py-2 w-full"
              >
                <option value="">Elige la categoría que queda</option>
                {destinosFusion.map((c) => (
                  <option key={c.id} value={c.id}>{c.padre_nombre ? `${c.padre_nombre} › ` : ''}{etiquetaCategoria(c)}</option>
                ))}
              </select>
            </div>
            {errorFusion && <p className="text-red-600 text-sm">{errorFusion}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setFusionando(null)} className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
                Cancelar
              </button>
              <button type="submit" disabled={!destinoFusion || guardandoFusion} className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700 disabled:opacity-50">
                {guardandoFusion ? 'Fusionando…' : 'Fusionar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      <Seccion tipo="egreso" titulo="Egresos" />
      <Seccion tipo="ingreso" titulo="Ingresos" />

      {categoriaVista && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">
                Movimientos de "{categoriaVista.nombre}"
                <span className="text-slate-400 font-normal text-sm"> · {totalCat} en total</span>
              </h2>
              <button onClick={cerrarVistaMovimientos} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Fecha</th>
                    <th className="p-3">Cuenta</th>
                    <th className="p-3">Descripción</th>
                    <th className="p-3 text-right">Monto</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {cargandoCat ? (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-slate-400">Cargando...</td>
                    </tr>
                  ) : movimientosCat.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-slate-400">Esta categoría todavía no tiene movimientos.</td>
                    </tr>
                  ) : (
                    movimientosCat.map((m) => (
                      <tr key={m.id} className="border-t hover:bg-slate-50 transition-colors">
                        <td className="p-3 whitespace-nowrap">{formatFecha(m.fecha)}</td>
                        <td className="p-3 whitespace-nowrap">{m.cuenta_nombre}</td>
                        <td className="p-3">{m.descripcion || '-'}</td>
                        <td className="p-3 text-right whitespace-nowrap">
                          {m.tipo === 'egreso' ? '-' : ''}
                          {m.cuenta_moneda === 'BS' ? 'Bs ' : '$'}{formatMonto(m.monto)}
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          <div className="flex items-center gap-3 justify-end">
                            <button onClick={() => empezarEdicionMov(m)} title="Editar" className="text-slate-400 hover:text-primary-600">
                              <Pencil size={15} />
                            </button>
                            <button onClick={() => eliminarMov(m.id)} title="Eliminar" className="text-slate-400 hover:text-red-600">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <Paginacion pagina={paginaCat} totalPaginas={totalPaginasCat} total={totalCat} onCambiarPagina={setPaginaCat} />
            </div>
          </div>
        </div>
      )}

      {editandoMovId && (
        <Modal title="Editando movimiento" onClose={() => setEditandoMovId(null)} maxWidth="max-w-2xl">
          <form onSubmit={guardarMov} className="flex flex-wrap items-end gap-2">
            <div className="min-w-[130px]">
              <label className="block text-xs text-slate-500 mb-1">Fecha</label>
              <input
                type="date"
                required
                value={formMov.fecha}
                onChange={(e) => setFormMov({ ...formMov, fecha: e.target.value })}
                className="border rounded-md px-2.5 py-1.5 w-full text-sm"
              />
            </div>
            <div className="min-w-[160px]">
              <label className="block text-xs text-slate-500 mb-1">Cuenta</label>
              <select
                required
                value={formMov.cuenta_id}
                onChange={(e) => setFormMov({ ...formMov, cuenta_id: e.target.value })}
                className="border rounded-md px-2.5 py-1.5 w-full text-sm"
              >
                {cuentas
                  .filter((c) => Number(c.activo) === 1 || String(c.id) === String(cuentaPreviaMov))
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}{Number(c.activo) === 1 ? '' : ' (inactiva)'}</option>
                  ))}
              </select>
            </div>
            <div className="min-w-[110px]">
              <label className="block text-xs text-slate-500 mb-1">Tipo</label>
              <select
                value={formMov.tipo}
                onChange={(e) => setFormMov({ ...formMov, tipo: e.target.value })}
                className="border rounded-md px-2.5 py-1.5 w-full text-sm"
              >
                <option value="ingreso">Ingreso</option>
                <option value="egreso">Egreso</option>
              </select>
            </div>
            <div className="min-w-[180px]">
              <label className="block text-xs text-slate-500 mb-1">Categoría</label>
              <select
                value={formMov.categoria_id}
                onChange={(e) => setFormMov({ ...formMov, categoria_id: e.target.value })}
                className="border rounded-md px-2.5 py-1.5 w-full text-sm"
              >
                <option value="">Sin categoría</option>
                {['egreso', 'ingreso'].map((tipo) => (
                  <React.Fragment key={tipo}>
                    {categoriasPrincipalesDe(tipo).map((padre) => (
                      <optgroup key={padre.id} label={`${tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} · ${padre.nombre}`}>
                        <option value={padre.id}>{padre.nombre} (general)</option>
                        {subCategoriasDe(padre.id).map((hijo) => (
                          <option key={hijo.id} value={hijo.id}>{hijo.nombre}</option>
                        ))}
                      </optgroup>
                    ))}
                  </React.Fragment>
                ))}
              </select>
            </div>
            <div className="min-w-[120px]">
              <label className="block text-xs text-slate-500 mb-1">Monto</label>
              <input
                type="number"
                step="0.01"
                required
                value={formMov.monto}
                onChange={(e) => setFormMov({ ...formMov, monto: e.target.value })}
                className="border rounded-md px-2.5 py-1.5 w-full text-sm text-right"
              />
            </div>
            <div className="w-full">
              <label className="block text-xs text-slate-500 mb-1">Descripción</label>
              <input
                value={formMov.descripcion}
                onChange={(e) => setFormMov({ ...formMov, descripcion: e.target.value })}
                className="border rounded-md px-2.5 py-1.5 w-full text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-primary-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-primary-700">
                Guardar
              </button>
              <button
                type="button"
                onClick={() => setEditandoMovId(null)}
                className="px-3 py-1.5 rounded-md text-sm border text-slate-600 hover:bg-slate-100"
              >
                Cancelar
              </button>
            </div>
            {errorMov && <p className="text-red-600 text-sm w-full">{errorMov}</p>}
          </form>
        </Modal>
      )}
      </>)}
    </div>
  );
}
